import React, { memo, useMemo } from 'react';
import { Box, createStyles, Icon, makeStyles, Chip } from '@material-ui/core';
import { RemoveCircleOutline } from '@material-ui/icons';
import Anesthesiologist from '@/types/Anesthesiologist';
import { useMutation, useQuery } from '@apollo/client';
import { list as listAnesthesiologists } from '@/graph/anesthesiologists';
import { editProcedureAnesthesiologist } from '@/graph/procedures';
import PopupSelectMenu from '../shared/PopupSelectMenu';
import AnesthesiaIcon from '@/assets/images/anesthesia.svg?react';
import { fullNameToLastNameRest } from '../shared/procedureUtils';

const AnesthesiologistName = ({
  date,
  procedureId,
  editableAnesthesiologist,
  anesthesiologistId,
  anesthesiologistName,
  condensView,
}: {
  date?: Date;
  procedureId: number;
  editableAnesthesiologist?: boolean;
  anesthesiologistId?: number;
  anesthesiologistName?: string;
  condensView?: boolean;
}) => {
  const classes = useStyles();

  const { data } = useQuery(listAnesthesiologists);
  const anesthesiologists: Anesthesiologist[] = useMemo(() => data?.anesthesiologists || [], [data?.anesthesiologists]);

  const options = useMemo(
    () =>
      data
        ? anesthesiologists.map(a => ({
            id: a.id,
            label: a.name,
          }))
        : undefined,
    [data, anesthesiologists]
  );

  const selectedId = useMemo(() => {
    if (anesthesiologistId != null) return anesthesiologistId;

    const label = (anesthesiologistName || '').toLowerCase();
    if (!label) return undefined;

    const match = anesthesiologists.find(a => {
      const lastName = (fullNameToLastNameRest(a.name) || '').toLowerCase();
      const fullName = (a.name || '').toLowerCase();
      return lastName === label || fullName === label;
    });

    return match?.id;
  }, [anesthesiologistId, anesthesiologists, anesthesiologistName]);

  const [setAnesthesiologist] = useMutation(editProcedureAnesthesiologist);

  const handleSelect = async (anesthesiologistId: number) => {
    if (!date) return;

    await setAnesthesiologist({
      variables: { id: procedureId, anesthesiologistId },
    });
  };

  const handleRemoveAnesthesiologist = async () => {
    if (!date) return;

    await setAnesthesiologist({
      variables: { id: procedureId, anesthesiologistId: null },
    });
  };

  return (
    <PopupSelectMenu
      options={options}
      selectedId={selectedId}
      editable={editableAnesthesiologist}
      onSelect={handleSelect}
      onRemove={editableAnesthesiologist ? handleRemoveAnesthesiologist : undefined}
      removeOption={
        <Chip
          icon={<RemoveCircleOutline className={classes.iconRemove} />}
          label={'Remove Slot'}
          size="small"
          className={classes.chip}
        />
      }
      renderTrigger={({ onClick }) => (
        <Box className={classes.editLabel} onClick={onClick}>
          {anesthesiologistName ? (
            (anesthesiologistName || '-').substring(0, condensView ? 15 : undefined)
          ) : editableAnesthesiologist ? (
            <Icon>
              <AnesthesiaIcon width={22} height={22} />
            </Icon>
          ) : (
            '-'
          )}
        </Box>
      )}
    />
  );
};

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      maxHeight: '38rem',
      overflow: 'hidden',
    },
    menu: {
      maxHeight: '28rem',
      overflowY: 'scroll',
    },
    textFieldRoot: {
      margin: '1rem',
      marginBottom: 0,
      padding: 0,
    },
    inputRoot: {
      width: '14rem',
      fontSize: '0.8125em',
      padding: '0.25em',
    },
    searchIcon: {
      marginLeft: '0.25em',
      marginRight: '0.25em',
    },
    inputAdornedStart: {
      paddingLeft: 0,
    },
    spinnerContainer: {
      padding: '1rem',
    },
    editLabel: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    chip: {
      backgroundColor: 'transparent',
      cursor: 'pointer',
      color: 'white',
    },
    iconRemove: {
      color: 'red',
    },
  })
);

export default memo(AnesthesiologistName);
